'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import s from './page.module.css';

export interface Step {
  title: string;
  body: string;
  points: readonly string[];
  scene: ReactNode;
}

/**
 * Scroll-driven walkthrough. On wide screens a sticky stage shows the scene
 * for whichever step sits in the middle of the viewport. Phones show each
 * scene above its step.
 */
export function StepStory({ steps }: { steps: readonly Step[] }) {
  const [current, setCurrent] = useState(0);
  const refs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = refs.current.indexOf(entry.target as HTMLLIElement);
          if (index >= 0) setCurrent(index);
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    for (const element of refs.current) if (element) observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
      <div className="hidden lg:block">
        <div
          className="sticky top-[calc(50vh-210px)] h-[420px]"
          aria-hidden="true"
        >
          <div className="relative h-full overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.01] shadow-[inset_0_1px_0_rgb(255_255_255/0.06)]">
            <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_40%,color-mix(in_oklab,var(--accent-50)_10%,transparent),transparent_70%)]" />
            {steps.map((step, index) => (
              <div
                key={step.title}
                className={cn(s.storyScene, 'p-8')}
                data-current={index === current}
              >
                {step.scene}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative">
        <span
          aria-hidden="true"
          className="absolute top-2 bottom-2 left-[15px] hidden w-0.5 rounded-full bg-white/10 lg:block"
        >
          <span
            className={cn(
              s.storyProgress,
              'absolute inset-0 rounded-full bg-[linear-gradient(var(--sun-50),var(--accent-50))]'
            )}
            style={{ scale: `1 ${(current + 1) / steps.length}` }}
          />
        </span>
        <ol className="space-y-16 lg:space-y-0">
          {steps.map((step, index) => (
            <li
              key={step.title}
              ref={element => {
                refs.current[index] = element;
              }}
              className={cn(
                s.storyStep,
                'lg:flex lg:min-h-[70vh] lg:flex-col lg:justify-center lg:pl-14'
              )}
              data-current={index === current}
            >
              <div
                className="mb-8 flex h-72 items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 lg:hidden"
                aria-hidden="true"
              >
                {step.scene}
              </div>
              <span className="text-accent-50 text-sm font-semibold tabular-nums">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-2 text-[clamp(1.6rem,1.2rem+1.5vw,2.25rem)] leading-tight font-semibold tracking-[-0.03em] text-white">
                {step.title}
              </h3>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                {step.body}
              </p>
              <ul className="mt-6 space-y-3">
                {step.points.map(point => (
                  <li
                    key={point}
                    className="flex gap-3 text-[15px] text-white/75"
                  >
                    <span className="bg-accent-50/15 text-accent-50 mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full">
                      <Check className="size-3" aria-hidden="true" />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
